import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCreateOrganization, useAssignOrgRole } from '../api/queries/organizations';
import { PhoneInput } from './PhoneInput';
import { stripPhoneForStorage } from '../utils/phone';
import { RolePills } from './RolePills';
import { LookupSelect } from './LookupSelect';
import { Modal } from './Modal';
import { AddressFields } from './AddressFields';
import { WebsiteInput } from './WebsiteInput';
import {
  addressValueToColumns,
  emptyAddressValue,
  isAddressEmpty,
  type AddressValue,
} from '../utils/address';
import { normalizeWebsite } from '../utils/website';
import type { OrganizationStatus, RoleBadge } from '@stfors/shared-types';
import '../pages/NewClientPage.css';

interface AddCompanyModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new org id after a successful create. */
  onCreated?: (orgId: number) => void;
}

/**
 * Entity-first "Add Company": create the organization once, then tag it with
 * any of Client / Contractor / Vendor (each becomes a minimal company-kind
 * role row pointing at the new org). Role-specific detail is filled later on
 * the company page; the only inline requirement is an email when Contractor
 * is selected (the contractors table requires one).
 */
export function AddCompanyModal({ open, onClose, onCreated }: AddCompanyModalProps): JSX.Element {
  const createOrg = useCreateOrganization();
  const assignRole = useAssignOrgRole();

  const [name, setName] = useState('');
  const [dba, setDba] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [ein, setEin] = useState('');
  const [industry, setIndustry] = useState('');
  // Structured from the start, like every other address surface. The free-text
  // blob this used to post could only be read back as a string — the company
  // page then offered to auto-parse what we could have captured properly here.
  const [address, setAddress] = useState<AddressValue>(emptyAddressValue);
  const [status, setStatus] = useState<OrganizationStatus>('active');
  const [roles, setRoles] = useState<RoleBadge[]>([]);
  const [error, setError] = useState<string | null>(null);

  const busy = createOrg.isPending || assignRole.isPending;

  const reset = () => {
    setName(''); setDba(''); setEmail(''); setPhone(''); setWebsite('');
    setEin(''); setIndustry(''); setAddress(emptyAddressValue()); setStatus('active');
    setRoles([]); setError(null);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError('Company name is required.');
      return;
    }
    if (roles.includes('contractor') && email.trim().length === 0) {
      setError('An email is required to tag this company as a Contractor.');
      return;
    }
    try {
      // Normalized again here, not only on the field's blur: a keyboard submit
      // (Enter from another field) never blurs the website box.
      const cols = addressValueToColumns(address);
      const res = await createOrg.mutateAsync({
        name: name.trim(),
        dba_name: dba.trim() || undefined,
        email: email.trim() || undefined,
        phone: stripPhoneForStorage(phone) || undefined,
        website: normalizeWebsite(website) || undefined,
        ein: ein.trim() || undefined,
        industry: industry.trim() || undefined,
        // POST omits blanks (it rejects null on these), so send the address
        // only when there is one — see frontend-patterns.md §6.1.
        ...(isAddressEmpty(address) ? {} : {
          address_line1: cols.line1 ?? undefined,
          address_line2: cols.line2 ?? undefined,
          city: cols.city ?? undefined,
          state: cols.state ?? undefined,
          postal_code: cols.postal_code ?? undefined,
          country: cols.country ?? undefined,
        }),
        status,
      });
      const orgId = res.organization.id;

      // Roles are attached after the org exists. If one fails, the company is
      // already created — tell the user so they can finish on the company page.
      try {
        for (const role of roles) {
          await assignRole.mutateAsync({
            orgId,
            role,
            email: role === 'contractor' ? email.trim() : undefined,
          });
        }
      } catch (roleErr) {
        const m = roleErr instanceof ApiError ? roleErr.message : 'a role could not be added';
        setError(`Company created, but ${m}. Finish tagging it from its page.`);
        onCreated?.(orgId);
        return;
      }

      reset();
      onCreated?.(orgId);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
        setError(err.message);
        return;
      }
      setError('Could not create the company. Try again.');
    }
  };

  return (
    <Modal
      title="Add Company"
      open={open}
      onClose={close}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-company-form"
            className="btn-cyan-primary"
            disabled={busy || name.trim().length === 0}
          >
            {busy ? 'Creating…' : 'Create company'}
          </button>
        </>
      }
    >
      <form id="add-company-form" className="new-client-form" onSubmit={onSubmit} noValidate style={{ padding: 0 }}>
        <div className="new-client-row">
          <label className="new-client-field">
            <span>Company name *</span>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
          </label>
          <label className="new-client-field">
            <span>Doing business as</span>
            <input className="form-input" value={dba} onChange={(e) => setDba(e.target.value)} maxLength={200} />
          </label>
        </div>
        <div className="new-client-row">
          <label className="new-client-field">
            <span>Email</span>
            <input className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} />
          </label>
          <label className="new-client-field">
            <span>Phone</span>
            <PhoneInput className="form-input" value={phone} onChange={setPhone} />
          </label>
        </div>
        <div className="new-client-row">
          <label className="new-client-field">
            <span>Website</span>
            <WebsiteInput value={website} onChange={setWebsite} disabled={busy} />
          </label>
          <label className="new-client-field">
            <span>EIN</span>
            <input className="form-input" value={ein} onChange={(e) => setEin(e.target.value)} placeholder="XX-XXXXXXX" maxLength={20} />
          </label>
        </div>
        <div className="new-client-row">
          <label className="new-client-field">
            <span>Industry</span>
            <input className="form-input" value={industry} onChange={(e) => setIndustry(e.target.value)} maxLength={100} />
          </label>
          <label className="new-client-field">
            <span>Status</span>
            <LookupSelect category="organization_status" value={status} onChange={setStatus} className="form-input" aria-label="Status" />
          </label>
        </div>
        <div className="new-client-field new-client-field-full">
          <span>Address</span>
          <AddressFields value={address} onChange={setAddress} disabled={busy} idPrefix="Company address" />
        </div>
        <div className="new-client-field new-client-field-full">
          <span>Roles (optional)</span>
          <RolePills scope="company" value={roles} onChange={setRoles} disabled={busy} />
          {roles.includes('contractor') && (
            <p className="field-error" style={{ color: 'var(--color-silver-metallic)' }}>
              Tagging as Contractor uses the email above (required).
            </p>
          )}
        </div>
        {error && <p className="new-client-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
